import { useEffect, useState, type ReactNode } from "react";
import { AppShell } from "../components/AppShell";
import { CompatibilityPage, HomePage, PrivacyPage } from "../components/StaticPages";
import { ActivityPage } from "../features/activity/ActivityPage";
import {
  ActiveLinksPanel,
  LinkDownloadPanel,
  LinkSharePanel,
} from "../features/link-share";
import {
  QuotationWorkspace,
  type QuotationHandoffFile,
  type QuotationHandoffMode,
} from "../features/quotation";
import { SettingsPage } from "../features/settings/SettingsPage";
import { QrTransferWorkspace, type TransferInputFile } from "../features/transfer";
import { navigate, useHashRoute } from "./router";

interface RouteView {
  title: string;
  eyebrow: string;
  content: ReactNode;
}

function NotFoundPage() {
  return (
    <section className="empty-panel">
      <h2>Page not found</h2>
      <p>The requested local app route does not exist.</p>
      <button
        className="button button-primary"
        type="button"
        onClick={() => navigate("/")}
      >
        Return home
      </button>
    </section>
  );
}

export function App() {
  const route = useHashRoute();
  const [handoffFiles, setHandoffFiles] = useState<TransferInputFile[]>([]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    const frame = window.requestAnimationFrame(() => {
      document.querySelector<HTMLElement>("#page-title")?.focus({
        preventScroll: true,
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [route.path]);

  const handOffQuotation = (file: QuotationHandoffFile, mode: QuotationHandoffMode) => {
    setHandoffFiles([file]);
    navigate(mode === "qr" ? "/transfer?mode=qr" : "/link");
  };

  let view: RouteView;
  switch (route.path) {
    case "/":
      view = {
        title: "Home",
        eyebrow: "Quotation & Transfer",
        content: <HomePage />,
      };
      break;
    case "/quotations":
      view = {
        title: "Quotations",
        eyebrow: "Local document workspace",
        content: <QuotationWorkspace onSendFile={handOffQuotation} />,
      };
      break;
    case "/transfer": {
      const resumeSessionId = route.search.get("resume") ?? undefined;
      view = {
        title: "File Transfer",
        eyebrow: "Device-to-device QR",
        content: (
          <QrTransferWorkspace
            initialFiles={handoffFiles}
            {...(resumeSessionId === undefined ? {} : { resumeSessionId })}
            route="send"
          />
        ),
      };
      break;
    }
    case "/receive":
      view = {
        title: "Receive by QR",
        eyebrow: "Invited local session",
        content: (
          <QrTransferWorkspace invitationRoute={window.location.hash} route="receive" />
        ),
      };
      break;
    case "/link":
      view = {
        title: "Send via link",
        eyebrow: "Browser-encrypted temporary delivery",
        content: <LinkSharePanel files={handoffFiles} />,
      };
      break;
    case "/download":
      view = {
        title: "Receive shared file",
        eyebrow: "Encrypted bearer link",
        content: <LinkDownloadPanel fragment={window.location.hash} />,
      };
      break;
    case "/activity":
      view = {
        title: "Activity",
        eyebrow: "This device",
        content: (
          <div className="activity-route">
            <ActivityPage />
            <ActiveLinksPanel />
          </div>
        ),
      };
      break;
    case "/settings":
      view = {
        title: "Settings",
        eyebrow: "Local data controls",
        content: <SettingsPage />,
      };
      break;
    case "/privacy":
      view = {
        title: "Privacy",
        eyebrow: "Clear data boundaries",
        content: <PrivacyPage />,
      };
      break;
    case "/compatibility":
      view = {
        title: "Compatibility",
        eyebrow: "MVP browser support",
        content: <CompatibilityPage />,
      };
      break;
    default:
      view = {
        title: "Not found",
        eyebrow: "Local route",
        content: <NotFoundPage />,
      };
  }

  useEffect(() => {
    document.title = `${view.title} | Quotation & Transfer`;
  }, [view.title]);

  return (
    <AppShell activePath={route.path} eyebrow={view.eyebrow} title={view.title}>
      {view.content}
    </AppShell>
  );
}
